import React, { FC, useEffect, useState, useMemo, useRef } from 'react'
import dynamic from 'next/dynamic'
import { GetServerSideProps } from 'next'
import { VideoObject } from 'schema-dts'
import {
    AdProps,
    AdType,
    LabelProps,
    MetaDataProps,
    TagProps,
    TeaserProps,
} from '@sport1/types/web'
import colorPattern from '@sport1/news-styleguide/colorPattern'
import Head from 'next/head'
import dayjs from 'dayjs'
import getVideoObject from '@/components/LinkedData/getVideoObject'
import { getTagBySlug } from '@/pages/api/cms/content/tag/[id]'
import { getSport1Navigation } from '@/pages/api/cms/navigation'
import { getRecommendedVideos, getVideo } from '@/pages/api/cms/video/[id]'
import { PageProps } from '@/types/page'
import { Video } from '@/types/video'
import Config from '@/utils/Config'
import { setPageAdsActive } from '@/utils/ads/setAdStatusInContent'
import handleRejection, { PageNotFound } from '@/utils/errors/handleRejection'
import extendVideos from '@/utils/video/extendVideos'
import getVideoLength from '@/utils/video/getVideoLength'
import { takeLastTagWithKeys } from '@/helpers/tagsHelper'
import { getVideoSlug } from '@/helpers/slugHelper'
import { RecommendedVideos } from '@/helpers/videoHelper/prepareVideoPlaylist/types'
import useVideoPlaylist from '@/hooks/useVideoPlaylist'
import { sport1Bold, sport1Regular } from '@/helpers/fonts'
import DisplayAd from '@/components/Ads/AdSlot/DisplayAd'
import { transformImageUrlToSize, transformImageUrlToSrcSet } from '@/helpers/imageHelper'
import { useTopMediaDimensions } from '@/hooks/useTopMediaDimensions'
import { TeaserSizeTypes } from '@/types/teaser'

const AdSlot = dynamic(() => import('@/components/Ads/AdSlot'))
const Font = dynamic(() => import('@sport1/news-styleguide/core/Theme/Fonts/Font'))
const JWPlayer = dynamic(() => import('@/components/Player/JWPlayer/JWPlayer'))
const NonFlexingContainer = dynamic(
    () => import('@sport1/news-styleguide/core/Layouts/NonFlexingContainer')
)
const FlexingContainer = dynamic(() => import('@sport1/news-styleguide/FlexingContainer'))
const TeaserInfo = dynamic(() => import('@sport1/news-styleguide/TeaserInfo'))
const StandardLayout = dynamic(() => import('@/layouts/Standard'))
const Teaser = dynamic(() => import('@/components/Teaser'))

type Props = Pick<PageProps, 'navigation'> & {
    linkedData?: Array<VideoObject | null>
    ads?: AdProps[]
    meta: MetaDataProps
    recommendedVideos?: RecommendedVideos
    video: Video & { videoTag?: TagProps | LabelProps }
}

const Index: FC<Props> = ({ navigation, recommendedVideos, video }) => {
    const [isJWPlayerLoaded, setIsJWPlayerLoaded] = useState(false)
    const { playlist, runningVideo, setRunningVideo } = useVideoPlaylist({
        video,
        recommendedVideos,
    })
    const { width, height } = useTopMediaDimensions()
    const image = video.imageUrl && transformImageUrlToSize(video.imageUrl, width, height)
    const srcSet = video.imageUrl && transformImageUrlToSrcSet(video.imageUrl, width, height)
    const shouldChangeUrl = useRef(false)

    useEffect(() => {
        if (shouldChangeUrl.current) {
            window.history.replaceState(
                null,
                runningVideo.title,
                getVideoSlug(runningVideo as unknown as TeaserProps)
            )
        } else {
            shouldChangeUrl.current = true
        }
    }, [runningVideo, shouldChangeUrl])

    const OutbrainWidget = useMemo(
        () => (
            <AdSlot
                ad={{ adType: AdType.OUTBRAIN_AD, widgetIdentifiers: { web: 'AR_2' }, adKeys: [] }}
                marginTop="spacing-6"
            />
        ),
        []
    )
    return (
        <StandardLayout
            navigation={{
                ...navigation,
                breadCrumbs: { pageTitle: runningVideo?.seoTitle ?? runningVideo.title },
            }}
            mainContenMaxWidth={1572}
        >
            <NonFlexingContainer
                backgroundColor="pearl"
                horizontal
                flexWrap={['wrap', 'wrap', 'wrap', 'nowrap']}
                width={['100%', 604, 762, 1155, 1548]}
                marginX="auto"
            >
                <NonFlexingContainer width="100%">
                    <NonFlexingContainer testID="video-player" className="video-aspect-ratio">
                        <Head>
                            <link
                                rel="preload"
                                fetchpriority="high"
                                href={video.imageUrl}
                                as="image"
                            />
                        </Head>
                        <NonFlexingContainer position="relative">
                            <FlexingContainer
                                position="absolute"
                                top={0}
                                right={0}
                                left={0}
                                bottom={0}
                                zIndex={6}
                            >
                                <JWPlayer
                                    hasRevolverPlay
                                    trackingData={runningVideo.tracking}
                                    autostart={true}
                                    id={runningVideo.id as string}
                                    playlist={playlist}
                                    ad={runningVideo.meta?.ad}
                                    duration={getVideoLength(runningVideo)}
                                    setRunningVideo={setRunningVideo}
                                    type="VoD"
                                    callback={() => setIsJWPlayerLoaded(true)}
                                />
                            </FlexingContainer>
                            {!isJWPlayerLoaded && (
                                <img
                                    fetchPriority="high"
                                    src={image}
                                    width={width}
                                    height={height}
                                    srcSet={srcSet}
                                    alt={video.description ?? ''}
                                    style={{
                                        height: 'auto',
                                        width: '100%',
                                    }}
                                />
                            )}
                        </NonFlexingContainer>
                    </NonFlexingContainer>
                    <NonFlexingContainer
                        padding={['spacing-6', 'spacing-9', 'spacing-9']}
                        backgroundColor={colorPattern.smoke}
                    >
                        <Font
                            fontVariant={[
                                'Title-M',
                                'Headline-S',
                                'Headline-L',
                                'Headline-L',
                                'Display-L',
                            ]}
                            fontFamilyVariant="Sport1-Bold-Headline"
                            nextFontFamilyVariant={sport1Bold.style.fontFamily}
                            testID="video-title"
                            webHeadlineType="h2"
                            color={colorPattern.pearl}
                        >
                            {runningVideo.title}
                        </Font>
                        {runningVideo.itemDescription && (
                            <NonFlexingContainer testID="video-description">
                                <Font
                                    nextFontFamilyVariant={sport1Regular.style.fontFamily}
                                    fontVariant={[
                                        'Body-S',
                                        'Body-L',
                                        'Title-L',
                                        'Title-L',
                                        'Title-L',
                                    ]}
                                    color={colorPattern.pearl}
                                    marginTop={['spacing-3', 'spacing-6', 'spacing-9', 'spacing-9']}
                                    marginBottom={[
                                        'spacing-6',
                                        'spacing-6',
                                        'spacing-9',
                                        'spacing-9',
                                    ]}
                                >
                                    {runningVideo.itemDescription}
                                </Font>
                            </NonFlexingContainer>
                        )}
                        <NonFlexingContainer
                            borderBottom={[1, 1, 1, 0]}
                            borderColor="onyx-opacity-12"
                            centerVertical
                            testID="video-date"
                        >
                            <TeaserInfo
                                imageUrl={runningVideo.videoTag?.iconUrl}
                                name={runningVideo.videoTag?.title}
                                published={
                                    runningVideo.publishedAt
                                        ? dayjs(
                                              runningVideo.publishedAt,
                                              "YYYY-MM-DD'T'HH:mm:ss.SSS+Z"
                                          )
                                              .tz()
                                              .format('DD.MM.YY')
                                        : undefined
                                }
                                size={['TeaserInfoS', 'TeaserInfoS', 'TeaserInfoL', 'TeaserInfoL']}
                            />
                        </NonFlexingContainer>
                    </NonFlexingContainer>
                </NonFlexingContainer>

                <NonFlexingContainer
                    minWidth={['100%', '100%', '100%', 369]}
                    maxWidth={['100%', '100%', '100%', 369]}
                >
                    <NonFlexingContainer
                        maxWidth="100%"
                        maxHeight={['100%', '100%', '100%', '661px']}
                        marginTop={['spacing-6', 'spacing-6', 'spacing-6', 'spacing-none']}
                        id="piano_video-related-content"
                        testID="video-related-content"
                        overflow="auto"
                        className="scrollbar-visible, scrollbar-webkit"
                        marginBottom={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-9']}
                        marginLeft={['spacing-none', 'spacing-none', 'spacing-none', 'spacing-9']}
                    >
                        {recommendedVideos
                            ?.filter(item => item.id !== runningVideo.id)
                            .map(rec => (
                                <NonFlexingContainer
                                    key={rec.id}
                                    marginBottom={[
                                        'spacing-6',
                                        'spacing-6',
                                        'spacing-6',
                                        'spacing-9',
                                    ]}
                                    testID="teaser-video"
                                >
                                    <Teaser
                                        size={TeaserSizeTypes.LIST}
                                        teaser={rec}
                                        backgroundColor="pearl"
                                    />
                                </NonFlexingContainer>
                            ))}
                    </NonFlexingContainer>
                    <NonFlexingContainer display={['none', 'none', 'none', 'flex']}>
                        <DisplayAd
                            placement="rectangle"
                            placementId="rectangle"
                            disableBottomSpace
                        />
                    </NonFlexingContainer>
                </NonFlexingContainer>
                <NonFlexingContainer width="100%" display={['flex', 'flex', 'flex', 'none']}>
                    <DisplayAd placement="topmobile" placementId="topmobile2" disableBottomSpace />
                </NonFlexingContainer>
            </NonFlexingContainer>
            <NonFlexingContainer display={['none', 'none', 'none', 'flex']}>
                <DisplayAd
                    placement="banner"
                    placementId="banner2"
                    disableBottomSpace
                    disableTopSpace
                />
            </NonFlexingContainer>
            <NonFlexingContainer paddingX={['spacing-7', 'spacing-7', 'spacing-9', 'spacing-none']}>
                <NonFlexingContainer
                    id="piano_video-page-teaser-list"
                    overflow="hidden"
                    width={['100%', 604, 762, 1155, 1548]}
                    marginX="auto"
                />
            </NonFlexingContainer>
            {OutbrainWidget}
        </StandardLayout>
    )
}
export const getServerSideProps: GetServerSideProps<Props | PageNotFound> = async ctx => {
    const videoId = (ctx.query.slug as string[]).join('/').split('__')[1]

    let navigationData, tag, video
    try {
        ;[navigationData, tag, video] = await Promise.all([
            getSport1Navigation(),
            getTagBySlug('/tv-video'),
            getVideo(videoId),
        ])
    } catch (error) {
        return handleRejection(error, { videoId })
    }

    video.meta?.tracking?.push({ key: 'page_withvideo', value: 'true' })
    await extendVideos([video])
    setPageAdsActive(video.meta || {}, Config.ADS_ACTIVE)

    const deeplink = `sport1://video/${videoId}`
    ctx.res.setHeader('deeplink', deeplink)

    const videoTag = takeLastTagWithKeys(video?.tags, ['iconUrl', 'title'])

    const props = {
        deeplink,
        layoutData: video,
        linkedData: [getVideoObject(video)],
        meta: video.meta || {},
        navigation: {
            ...navigationData,
            tag,
            breadCrumbs: { pageTitle: video.seoTitle || video.title },
        },
        video: {
            ...video,
            videoTag,
        },
    }

    if (video?.recommendedVideosUrl) {
        const recommendedVideos = await getRecommendedVideos(
            `${video.recommendedVideosUrl}/extended/true`,
            videoId
        )
        if (recommendedVideos?.length) {
            await extendVideos(recommendedVideos as unknown as Video[])
        }
        return {
            props: {
                ...props,
                recommendedVideos,
            },
        }
    }
    return {
        props,
    }
}
export default Index
